import { capitalize } from 'utils';
import { i18n } from 'i18n';
import {
  AutoCreatedRecipeDto,
  BackfillRecipeDto,
  CreateBackfillRecipeCommand,
  FileResponse,
  PagedListOfBackfillRecipeDto,
  UpdateBackfillRecipeCommand
} from './api';
import {
  makeCreateMutation, makeDeleteMutation, makeGetQuery, makeListQuery, makeUpdateMutation,
  PageParams
} from './useApi';

const RECIPE_KEY = 'recipe';
export const RECIPES_KEY = 'recipes';

export interface RecipeMutation {
  projectId: string;
  recipeId?: string | null;
  data: CreateBackfillRecipeCommand[] | UpdateBackfillRecipeCommand[];
  context: { stopeName: string[] }
}
export const useCreateRecipeMutation = makeCreateMutation<string, RecipeMutation>(
  RECIPES_KEY,
  (api, scenarioId) => (mutation) => api.recipes.create(mutation.projectId, mutation.data, scenarioId),
  (mutation) => {
    const names = mutation.context.stopeName;
    if (names.length > 3) {
      return i18n._('Recipes successfully created for {count} stopes.', { count: names.length });
    }
    const stopeNames = names
      .map((name) => capitalize(name))
      .join(', ');
    return i18n._('A recipe was added to stopes:').concat(` ${stopeNames}.`);
  },
);

export const useUpdateRecipeMutation = makeUpdateMutation<void, RecipeMutation>(
  RECIPE_KEY, RECIPES_KEY,
  (api) => (mutation) => api.recipes.update(mutation.recipeId ?? '', mutation.data[0]),
  () => i18n._('Recipe updated.'),
);

export interface RecipesQueryParams {
  projectId: string;
  stopeId: string;
  scenarioId?: string;
}

export interface AutoCreatedRecipesQueryParams {
  projectId: string;
  autoRecipeBatchId?: number;
  scenarioId?: string;
}

export const useRecipesQuery = makeListQuery<RecipesQueryParams, BackfillRecipeDto[]>(
  RECIPES_KEY,
  (api, params) => () => api.recipes.getAll(params.projectId, params.stopeId, params.scenarioId),
);

export const useRecipeQuery = makeGetQuery<BackfillRecipeDto, string>(
  RECIPE_KEY,
  (api, entityId) => () => api.recipes.get(entityId),
);

export const useDeleteRecipeMutation = makeDeleteMutation<void, BackfillRecipeDto>(
  RECIPE_KEY,
  RECIPES_KEY,
  (api) => async (recipe) => {
    const result = await api.recipes.delete(recipe.entityId);
    return result;
  },
  () => i18n._('Recipe successfully deleted'),
  (mutation) => mutation.entityId,
);

export const useRestoreRecipeMutation = makeCreateMutation<FileResponse, string>(
  RECIPES_KEY,
  (api) => (recipeId) => api.recipes.restore(recipeId),
  () => i18n._('Recipe successfully restored'),
);

export const useAutoCreatedRecipesQuery = makeListQuery<AutoCreatedRecipesQueryParams, AutoCreatedRecipeDto[]>(
  RECIPES_KEY,
  (api, params) => () => api.recipes.getAllAutoCreated(params.projectId, params.scenarioId),
);

export type PaginatedRecipesQueryParams = RecipesQueryParams & PageParams & { searchTerm?: string };

export const usePaginatedRecipesQuery = makeListQuery<PaginatedRecipesQueryParams, PagedListOfBackfillRecipeDto>(
  RECIPES_KEY,
  (api, params) => () => api.recipes.getPaginated(params.projectId, params.stopeId, params.page, params.pageSize, params.searchTerm, params.scenarioId),
);

export const useUndoAutoRecipesMutation = makeDeleteMutation<FileResponse, AutoCreatedRecipesQueryParams>(
  RECIPE_KEY,
  RECIPES_KEY,
  (api, scenarioId) => async (recipe) => {
    const result = await api.recipes.undoAutoRecipes(recipe.projectId, recipe.autoRecipeBatchId as number, scenarioId ?? undefined);
    return result;
  },
  () => i18n._('Recipes successfully deleted'),
  (mutation) => mutation.projectId,
);

export interface AllProjectRecipesQueryParams {
  projectId: string;
  scenarioId?: string;
}

export const useAllProjectRecipesQuery = makeListQuery<AllProjectRecipesQueryParams, BackfillRecipeDto[]>(
  RECIPES_KEY,
  (api, params) => () => api.recipes.getAllByProject(params.projectId, params.scenarioId),
);
